<script lang="ts" module>
  import type { IconName } from './Icon.svelte';
  import type { LightingPreset, SalonType } from '../lib/types/wvm';

  interface Opt {
    st: SalonType;
    /** i18n key cho nhãn (dịch lúc render). */
    key: string;
    icon: IconName;
  }
  interface Grp {
    catKey: string;
    opts: Opt[];
  }
  export const MOTION_GROUPS: Grp[] = [
    { catKey: 'author.catDoor', opts: [
      { st: 3, key: 'author.mHingeL', icon: 'hingeL' },
      { st: 2, key: 'author.mHingeR', icon: 'hingeR' },
      { st: 4, key: 'author.mLiftDown', icon: 'lift' },
      { st: 5, key: 'author.mLiftUp', icon: 'lift' },
      { st: 6, key: 'author.mSlideL', icon: 'slide' },
      { st: 7, key: 'author.mSlideR', icon: 'slide' },
    ] },
    { catKey: 'author.catDrawer', opts: [
      { st: 8, key: 'author.mDrawerBody', icon: 'drawer' },
      { st: 11, key: 'author.mDrawerFront', icon: 'drawer' },
    ] },
    { catKey: 'author.catAdvanced', opts: [
      { st: 12, key: 'author.mAventosHS', icon: 'lift' },
      { st: 14, key: 'author.mAventosHF', icon: 'lift' },
      { st: 13, key: 'author.mPiston', icon: 'lift' },
      { st: 15, key: 'author.mDeepSlide', icon: 'slide' },
    ] },
  ];

  export const LIGHTINGS: { preset: LightingPreset; icon: IconName; labelKey: string; hintKey: string }[] = [
    { preset: 'even', icon: 'sun', labelKey: 'author.lightEven', hintKey: 'author.lightEvenHint' },
    { preset: 'studio', icon: 'bulb', labelKey: 'author.lightStudio', hintKey: 'author.lightStudioHint' },
    { preset: 'bright', icon: 'sunrise', labelKey: 'author.lightBright', hintKey: 'author.lightBrightHint' },
  ];
</script>

<script lang="ts">
  import Icon from './Icon.svelte';
  import { t } from '../i18n/index.svelte';

  /** Một nhóm chuyển động + các chi tiết thành viên (để hiện "mở cùng với …"). */
  interface MotionGroup {
    num: number;
    members: { id: number; name: string }[];
  }

  let {
    selectedName,
    selectedId,
    salonType,
    flip,
    swingOut,
    currentGroup,
    groups = [],
    saving,
    dirty,
    locked = false,
    lighting,
    lightsOn,
    lightIntensity,
    onAssign,
    onClear,
    onIndependent,
    onMerge,
    onFlip,
    onSwingOut,
    onSave,
    onLighting,
    onLightsToggle,
    onLightIntensity,
    onAiRender,
  }: {
    selectedName: string | null;
    selectedId: number | null;
    salonType: SalonType;
    flip: boolean;
    /** chiều mở ra/vào (depth) — chỉ áp cho cửa bản lề đứng 2/3, trực giao với flip. */
    swingOut: boolean;
    /** nhóm của chi tiết đang chọn (0 = chưa gán). */
    currentGroup: number;
    /** mọi nhóm chuyển động trong model + thành viên. */
    groups?: MotionGroup[];
    saving: boolean;
    dirty: boolean;
    locked?: boolean;
    lighting: LightingPreset;
    lightsOn: boolean;
    lightIntensity: number;
    onAssign: (st: SalonType) => void;
    onClear: () => void;
    /** tách chi tiết đang chọn ra nhóm RIÊNG (mở một mình). */
    onIndependent: () => void;
    /** gộp chi tiết đang chọn vào nhóm `g` (mở cùng các thành viên nhóm đó). */
    onMerge: (g: number) => void;
    onFlip: () => void;
    /** đảo chiều mở ra/vào (depth) cho cửa bản lề đứng. */
    onSwingOut: () => void;
    onSave: () => void;
    onLighting: (p: LightingPreset) => void;
    onLightsToggle: () => void;
    onLightIntensity: (value: number) => void;
    /** mở panel AI Render (04a). Vắng → ẩn nút. */
    onAiRender?: () => void;
  } = $props();

  const activeHintKey = $derived(LIGHTINGS.find((l) => l.preset === lighting)?.hintKey ?? '');

  // tên các chi tiết trong một nhóm, bỏ chính chi tiết đang chọn (để hiện "mở cùng với …").
  const otherNames = (g: MotionGroup) =>
    g.members
      .filter((m) => m.id !== selectedId)
      .map((m) => m.name)
      .join(', ');

  // thành viên nhóm hiện tại (kể cả chi tiết đang chọn).
  const currentMembers = $derived(groups.find((g) => g.num === currentGroup)?.members ?? []);
  // "mở riêng" = nhóm hiện tại chỉ có một mình chi tiết đang chọn (hoặc chưa có nhóm).
  const isIndependent = $derived(currentGroup <= 0 || currentMembers.length <= 1);
  // các nhóm KHÁC (đã có chuyển động) → đích để "mở cùng".
  const mergeTargets = $derived(groups.filter((g) => g.num !== currentGroup && g.members.length > 0));
</script>

<aside class="author">
  <div class="badge"><Icon name="save" size={14} /> {t('author.editBadge')}</div>

  {#if locked}
    <div class="locked-banner">🔒 {t('author.lockedBanner')}</div>
  {/if}

  {#if onAiRender}
    <button class="ai-render-btn" disabled={locked} onclick={onAiRender}>
      <Icon name="sparkles" size={16} /><span>{t('airender.open')}</span>
    </button>
  {/if}

  <div class="scroll">
    <h2>{t('author.motionTitle')}</h2>

    {#if !selectedName}
      <p class="hint">{t('author.selectPart')}</p>
    {:else}
      <p class="sel">{t('author.assigningTo')} <strong>{selectedName}</strong></p>

      {#each MOTION_GROUPS as g (g.catKey)}
        <div class="cat">{t(g.catKey)}</div>
        <div class="opts">
          {#each g.opts as o (o.st)}
            <button class:active={salonType === o.st} disabled={locked} onclick={() => onAssign(o.st)}>
              <Icon name={o.icon} size={16} /><span>{t(o.key)}</span>
            </button>
          {/each}
        </div>
      {/each}

      {#if salonType}
        <div class="cat">{t('author.groupRel')}</div>
        <p class="hint small">{t('author.groupRelHint')}</p>
        <div class="rel">
          <button
            class="rel-row"
            class:active={isIndependent}
            disabled={locked || isIndependent}
            onclick={onIndependent}
          >
            <Icon name="cube" size={16} />
            <span class="rel-main">
              <strong>{t('author.relIndependent')}</strong>
              <span class="rel-sub">{t('author.relIndependentSub')}</span>
            </span>
          </button>

          {#if !isIndependent}
            <!-- nhóm hiện tại của chi tiết: đang mở cùng các thành viên khác -->
            <div class="rel-row active current" aria-current="true">
              <Icon name="list" size={16} />
              <span class="rel-main">
                <strong>{t('author.relMergeWith')} {otherNames({ num: currentGroup, members: currentMembers })}</strong>
                <span class="rel-sub">{t('author.groupShort')} {currentGroup}</span>
              </span>
            </div>
          {/if}

          {#each mergeTargets as g (g.num)}
            <button class="rel-row" disabled={locked} onclick={() => onMerge(g.num)}>
              <Icon name="list" size={16} />
              <span class="rel-main">
                <strong>{t('author.relMergeWith')} {otherNames(g)}</strong>
                <span class="rel-sub">{t('author.groupShort')} {g.num}</span>
              </span>
            </button>
          {/each}
        </div>
      {/if}

      <div class="cat">{t('author.catOptions')}</div>
      {#if salonType === 2 || salonType === 3}
        <!-- Chiều mở RA/VÀO (depth) — trực giao với flip (trái/phải). Chỉ cửa bản lề đứng. -->
        <button
          class="swingout"
          class:active={swingOut}
          disabled={locked}
          title={t('author.swingOutHint')}
          onclick={onSwingOut}
        >
          <Icon name="flip" size={16} />
          <span>{t('author.swingOut')}</span>
        </button>
        <p class="hint small">{t('author.swingOutHint')}</p>
      {/if}
      <div class="row2">
        <button class:active={flip} disabled={locked} title={t('author.flipHint')} onclick={onFlip}><Icon name="flip" size={16} /><span>{t('author.flip')}</span></button>
        <button class="ghost" disabled={locked} onclick={onClear}><Icon name="close" size={16} /><span>{t('author.clearAssign')}</span></button>
      </div>
    {/if}

    <h2 class="sec">{t('author.lightTitle')}</h2>
    <p class="hint">{t('author.lightApplyAll')}</p>

    <button
      class="light-toggle"
      class:off={!lightsOn}
      disabled={locked}
      aria-pressed={!lightsOn}
      onclick={onLightsToggle}
    >
      <Icon name="power" size={16} />
      <span>{lightsOn ? t('author.lightOnState') : t('author.lightOffState')}</span>
    </button>
    <p class="hint small">{lightsOn ? t('author.lightOnHint') : t('author.lightOffHint')}</p>

    <div class="cat">{t('author.lightKind')}</div>
    <div class="lights3">
      {#each LIGHTINGS as l (l.preset)}
        <button
          class:active={lighting === l.preset}
          disabled={locked || !lightsOn}
          onclick={() => onLighting(l.preset)}
        >
          <Icon name={l.icon} size={16} /><span>{t(l.labelKey)}</span>
        </button>
      {/each}
    </div>
    {#if lightsOn && activeHintKey}
      <p class="hint small">{t(activeHintKey)}</p>
    {/if}

    <div class="cat">{t('author.lightIntensity')}</div>
    <label class="intensity">
      <input
        type="range"
        min="0"
        max="100"
        step="1"
        value={lightIntensity}
        disabled={locked || !lightsOn}
        aria-label={t('author.lightIntensity')}
        oninput={(e) => onLightIntensity(+e.currentTarget.value)}
      />
      <span>{lightIntensity}</span>
    </label>
    <p class="hint small">{t('author.lightIntensityHint')}</p>
  </div>

  <button class="save" disabled={locked || !dirty || saving} onclick={onSave}>
    <Icon name="save" size={18} />
    {saving ? t('author.saving') : t('author.save')}
  </button>
</aside>

<style>
  .author {
    position: absolute;
    top: 72px;
    right: var(--space-md);
    bottom: 84px;
    width: var(--size-side-panel);
    z-index: var(--z-panel);
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);
    padding: var(--space-md);
    background: var(--color-surface-glass);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-panel);
    box-shadow: var(--shadow-panel);
    backdrop-filter: blur(10px);
  }
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    padding: 4px 10px;
    border-radius: var(--radius-pill);
    background: var(--color-yellow-accent);
    color: var(--color-navy);
    font-size: var(--font-size-caption);
    font-weight: 700;
  }
  .ai-render-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    min-height: 38px;
    border: 1px solid var(--color-primary);
    border-radius: var(--radius-control);
    background: linear-gradient(135deg, var(--color-primary), var(--color-primary-accent));
    color: #fff;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
  }
  .ai-render-btn:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  .ai-render-btn:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .locked-banner {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    border-radius: var(--radius-control);
    background: #fff4e0;
    color: var(--color-warning);
    font-size: var(--font-size-caption);
    font-weight: 700;
    line-height: 1.4;
  }
  .opts button:disabled,
  .row2 button:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .opts button:disabled:hover,
  .row2 button:disabled:hover {
    border-color: var(--color-border);
    color: var(--color-text-secondary);
  }
  h2 {
    margin: 0;
    font-size: var(--font-size-body);
    font-weight: 700;
  }
  .hint,
  .sel {
    margin: 0;
    font-size: var(--font-size-caption);
    color: var(--color-text-secondary);
  }
  .sel strong {
    color: var(--color-text-primary);
  }
  .scroll {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    margin: 0 calc(-1 * var(--space-xs));
    padding: 0 var(--space-xs);
  }
  .cat {
    font-size: var(--font-size-caption);
    font-weight: 700;
    color: var(--color-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: var(--space-sm) 0 4px;
  }
  .opts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .opts button,
  .row2 button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 36px;
    padding: 0 8px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    background: var(--color-surface);
    color: var(--color-text-primary);
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 600;
    cursor: pointer;
    text-align: left;
  }
  .opts button span,
  .row2 button span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .opts button:hover,
  .row2 button:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  .opts button.active,
  .row2 button.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }
  .rel {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .rel-row {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 40px;
    padding: 6px 10px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    background: var(--color-surface);
    color: var(--color-text-primary);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .rel-row .rel-main {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.3;
  }
  .rel-row .rel-main strong {
    font-size: var(--font-size-caption);
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .rel-row .rel-sub {
    font-size: var(--font-size-caption);
    color: var(--color-text-secondary);
    font-weight: 600;
  }
  button.rel-row:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  button.rel-row:disabled {
    cursor: default;
  }
  /* nhóm hiện tại (đang mở cùng người khác): viền hổ phách trùng màu highlight 3D */
  .rel-row.active.current {
    border-color: #ff9500;
    background: #fff6e8;
    cursor: default;
  }
  .rel-row.active.current .rel-main strong {
    color: var(--color-navy);
  }
  /* "mở riêng" đang được chọn → primary, rõ trạng thái */
  .rel-row.active:not(.current) {
    border-color: var(--color-primary);
    background: var(--color-primary);
  }
  .rel-row.active:not(.current) .rel-main strong,
  .rel-row.active:not(.current) .rel-sub {
    color: #fff;
  }
  .swingout {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    min-height: 36px;
    padding: 0 10px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    background: var(--color-surface);
    color: var(--color-text-primary);
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 600;
    cursor: pointer;
    text-align: left;
  }
  .swingout:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  .swingout.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }
  .swingout:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .row2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  h2.sec {
    margin-top: var(--space-md);
    padding-top: var(--space-sm);
    border-top: 1px solid var(--color-border);
  }
  .hint.small {
    line-height: 1.45;
    margin: 4px 0 0;
  }
  .light-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 36px;
    margin-top: 6px;
    padding: 0 10px;
    border: 1px solid var(--color-primary);
    border-radius: var(--radius-control);
    background: var(--color-primary);
    color: #fff;
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 700;
    cursor: pointer;
  }
  .light-toggle.off {
    background: var(--color-surface);
    border-color: var(--color-border);
    color: var(--color-text-secondary);
  }
  .light-toggle:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .lights3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 6px;
  }
  .lights3 button {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    min-height: 44px;
    padding: 4px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    background: var(--color-surface);
    color: var(--color-text-primary);
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 600;
    cursor: pointer;
  }
  .lights3 button:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  .lights3 button.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }
  .lights3 button:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .intensity {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 2px 0;
  }
  .intensity input[type='range'] {
    flex: 1;
    min-width: 0;
    accent-color: var(--color-primary);
    cursor: pointer;
  }
  .intensity input[type='range']:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .intensity span {
    min-width: 3ch;
    text-align: right;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .save {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: var(--size-touch-min);
    border: none;
    border-radius: var(--radius-control);
    background: var(--color-primary);
    color: #fff;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
  }
  .save:disabled {
    background: var(--color-border);
    color: var(--color-text-secondary);
    cursor: default;
  }
  .save:not(:disabled):hover {
    background: var(--color-primary-accent);
  }

  @media (max-width: 767px) {
    .author {
      top: auto;
      right: var(--space-md);
      left: var(--space-md);
      bottom: 140px;
      width: auto;
      max-height: 52vh;
    }
  }
</style>
